/** @format */

import { isPlatformServer } from '@angular/common';
import {
	afterNextRender,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	effect,
	ElementRef,
	HostListener,
	inject,
	input,
	output,
	PLATFORM_ID,
	Renderer2,
	signal,
	Signal,
	viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { DeviceService } from '@shared/data-access/services';
import {
	CompareItemAttribute,
	CompareProductDetails,
	CompareProductItem,
	CompareService
} from '@compare/data-access';
import { CarouselButtonComponent } from '@shared/components';
import {
	HomefieldIconLibraryModule,
	homefieldIconSignAdd,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';

import { CompareGridProductCardComponent } from './compare-grid-product-card/compare-grid-product-card.component';
import { FulfillmentSectionComponent } from './fullfilment-section/fulfillment-section.component';
import { SpecsSectionComponent } from './specs-section/specs-section.component';
import { AttributeSectionComponent } from './attribute-section/attribute-section.component';
import { SkeletonAttributeSectionComponent } from './skeleton-attribute-section/skeleton-attribute-section.component';
import { SkeletonSpecsSectionComponent } from './skeleton-specs-section/skeleton-specs-section.component';
import { CompareBarProduct, ProductAvailabilityCollection } from '@shared/data-access/interfaces';
import { ApiFailureComponent } from './api-failure/api-failure.component';
import { NoProductsToCompareComponent } from './no-products/no-products.component';

type GridSection =
	| 'productCardsGridSection'
	| 'fulfillmentGridSection'
	| 'attributesGridSection'
	| 'specsGridSection';

@Component({
	selector: 'compare-components-compare-grid',
	imports: [
		AttributeSectionComponent,
		CarouselButtonComponent,
		CompareGridProductCardComponent,
		FulfillmentSectionComponent,
		SpecsSectionComponent,
		SkeletonAttributeSectionComponent,
		SkeletonSpecsSectionComponent,
		HomefieldIconLibraryModule,
		ApiFailureComponent,
		NoProductsToCompareComponent
	],
	templateUrl: './compare-grid.component.html',
	styleUrl: './compare-grid.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompareGridComponent {
	private readonly destroyRef = inject(DestroyRef);
	private readonly deviceService = inject(DeviceService);
	private readonly compareService = inject(CompareService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly hmfIconService = inject(HomefieldIconsLibraryService);
	private readonly renderer = inject(Renderer2);
	private readonly platformId = inject(PLATFORM_ID);

	readonly location = input<'PLP' | 'PDP'>('PLP');
	readonly compareProductDetails = input.required<CompareProductDetails>();
	readonly compareBarProducts = input.required<CompareBarProduct[]>();
	readonly compareProductsAvailability = input<ProductAvailabilityCollection | null>(null);
	readonly compareGridLoading = input.required<boolean>();
	readonly compareGridApiFailed = input.required<boolean>();

	readonly productRemoved = output<string>();
	readonly closeCompareGrid = output<void>();
	readonly tryAgainClicked = output<void>();

	readonly productCardsGridSection =
		viewChild<ElementRef<HTMLDivElement>>('productCardsGridSection');
	readonly specsGridSection = viewChild<ElementRef<HTMLDivElement>>('specsGridSection');
	readonly fulfillmentGridSection =
		viewChild<ElementRef<HTMLDivElement>>('fulfillmentGridSection');
	readonly attributesGridSection = viewChild<ElementRef<HTMLDivElement>>('attributesGridSection');
	readonly scrollIndicator = viewChild<ElementRef<HTMLDivElement>>('scrollIndicator');
	readonly hiddenIntersectionElement = viewChild<ElementRef<HTMLElement>>(
		'hiddenIntersectionElement'
	);

	private readonly resizeSubject = new Subject<void>();

	readonly coalescedProductDetails: Signal<CompareProductItem[]> = computed(() => {
		if (!this.compareProductDetails()?.products?.length) {
			return [];
		}

		return this.compareService.coalesceAllProductAttributes(
			this.compareProductDetails()?.products
		);
	});

	readonly attributeSectionExpandedList = signal<{ [key: string]: boolean }>({});
	readonly currentElement = signal<GridSection>('productCardsGridSection');
	readonly deviceType = signal('');
	readonly isDrawerScrollable = signal(false);
	readonly isScrollLeftAllowed = signal(false);
	readonly isScrollRightAllowed = signal(false);
	readonly isSticky = signal(false);
	readonly visibleContentPercentage = signal(0);
	readonly apiRetryCounter = signal(0);

	constructor() {
		this.hmfIconService.registerIcons([homefieldIconSignAdd]);
		effect(() => {
			this.setGridTemplateAttributeRows();
		});

		afterNextRender(() => {
			this.resizeSubject
				.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(200))
				.subscribe(() => {
					this.setDrawerScrollSettings();
				});

			// giving a chance to the drawer to completely open
			// let the first execution thread pass over this function, then let the next one call this function
			setTimeout(() => {
				this.setDrawerScrollSettings();
			}, 0);
		});
	}

	verticalScrolling(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		const drawerScrollDisplacement = document.getElementById('drawer-content').scrollTop;

		if (this.visibleContentPercentage() > 84) {
			// percentage 0-100%
			// if the visible content is more than 84% of the total content, this means the scrollable distance is small
			// if the scrollable distance is small, we don't want to shrink the product card area
			// this is done because the change in height causes the scroll displacement to reset
			// and product cards start going between shirnking and not shrinking
			return;
		}

		if (drawerScrollDisplacement > 0) {
			this.isSticky.set(true);
		} else {
			this.isSticky.set(false);
		}
	}

	private get sectionElements(): Record<GridSection, HTMLDivElement> {
		return {
			productCardsGridSection: this.productCardsGridSection()?.nativeElement,
			fulfillmentGridSection: this.fulfillmentGridSection()?.nativeElement,
			attributesGridSection: this.attributesGridSection()?.nativeElement,
			specsGridSection: this.specsGridSection()?.nativeElement
		};
	}

	onHorizontalScroll(event: Event): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		for (const [name, element] of Object.entries(this.sectionElements)) {
			if (name !== this.currentElement() && !!element) {
				element.scrollLeft = (event.target as Element).scrollLeft;
			}
		}

		this.updateScrollIndicator();
	}

	scrollToSide(direction: 'left' | 'right'): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		// determine the scroll distance based on the number of items in the compare bar
		// this is to ensure that the scroll distance is equal to the width of one item
		// in the compare bar, so that when the user clicks the scroll button, it scrolls one item at a time
		const scrollDistance =
			this.sectionElements.productCardsGridSection.scrollWidth /
			this.compareBarProducts().length;

		const scrollAmount =
			direction === 'right'
				? this.sectionElements.productCardsGridSection.scrollLeft + scrollDistance
				: this.sectionElements.productCardsGridSection.scrollLeft - scrollDistance;

		for (const element of Object.values(this.sectionElements)) {
			if (element) {
				element.scrollTo({ left: scrollAmount, behavior: 'smooth' });
			}
		}
	}

	// As the user touches or mouses over the upper or lower section of the drawer,
	// we need to update the scroll position of the other section
	updateCurrentElement(element: GridSection): void {
		this.currentElement.set(element);
	}

	onDrawerClosed(): void {
		this.isSticky.set(false);
		this.isDrawerScrollable.set(false);
		this.isScrollRightAllowed.set(false);
		this.isScrollLeftAllowed.set(false);
	}

	removeProductFromCompareGrid(ecode: string, index: number): void {
		const productNameSectionNodes: NodeListOf<HTMLElement> =
			this.productCardsGridSection().nativeElement.querySelectorAll(
				'.compare-product-name-section'
			);

		// if there are multiple product cards, we need to focus on the card that will end up as the first on the list
		if (productNameSectionNodes.length > 1) {
			const productNameSection: HTMLElement =
				index === 0 ? productNameSectionNodes[1] : productNameSectionNodes[0];
			productNameSection.focus();
		}

		this.productRemoved.emit(ecode);
		this.updateScrollIndicator();
	}

	closeDrawer(): void {
		this.closeCompareGrid.emit();
	}

	handleAttributeExpand(event: { expanded: boolean; identifier: string }): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		const newAttributeSectionExpandedList = {
			...this.attributeSectionExpandedList(),
			[event.identifier]: event.expanded
		};

		this.attributeSectionExpandedList.set(newAttributeSectionExpandedList);
	}

	@HostListener('window:resize', ['$event'])
	onResize(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.resizeSubject.next();
	}

	private setVisibleContentPercentage(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		const drawerContentEl: Element = document.getElementById('drawer-content');

		const percentage = (drawerContentEl.clientHeight / drawerContentEl.scrollHeight) * 100;

		this.visibleContentPercentage.set(percentage);
	}

	private setDrawerScrollSettings(): void {
		this.deviceType.set(this.deviceService.getDeviceType());
		this.setVisibleContentPercentage();
		this.updateScrollIndicator();
	}

	private updateScrollIndicator(): void {
		/**
		 * If not browser environment or grid isn't rendered (i.e. the API call failed or 0 products), we should short
		 * circuit the function so we don't attempt to use an element that doesn't exist (which would trigger an error.)
		 */
		if (isPlatformServer(this.platformId) || !this.productCardsGridSection()) {
			return;
		}

		this.setDrawerHorizontallyScrollable();
		this.setHorizontalScrollbarPosition();
	}

	private setDrawerHorizontallyScrollable(): void {
		// this compares the scrollWidth (including overflow) to the clientWidth (visible area)
		// to determine whether to show the custom-made scroll bar
		const isScrollable =
			this.productCardsGridSection().nativeElement.scrollWidth >
			this.productCardsGridSection().nativeElement.clientWidth;
		this.isDrawerScrollable.set(isScrollable);
		this.cdr.detectChanges();
	}

	private setHorizontalScrollbarPosition(): void {
		// this is to calculate the percentage scrolled horizontally to decide whether
		// to show the left or right buttons of the scroll indicator
		const scrollWidth =
			this.productCardsGridSection().nativeElement.scrollWidth -
			this.productCardsGridSection().nativeElement.clientWidth;
		const scrollPercentage =
			this.productCardsGridSection().nativeElement.scrollLeft / scrollWidth;
		const scrollBarMargins = this.deviceType() === 'mobile' ? 32 : 48;

		const indicatorWidth =
			(this.productCardsGridSection().nativeElement.clientWidth /
				this.productCardsGridSection().nativeElement.scrollWidth) *
			this.productCardsGridSection().nativeElement.clientWidth;
		this.isScrollRightAllowed.set(scrollPercentage < 1);
		this.isScrollLeftAllowed.set(scrollPercentage > 0);

		// if the drawer is scrollable, we show a scrollbar below the upper part to indicate to the user the scroll position
		// this horizontal scrollbar is not interactible, the user is expected to use the buttons or the touch screen if mobile device is used
		if (this.isDrawerScrollable()) {
			this.scrollIndicator().nativeElement.style.width = `${indicatorWidth}px`;
			this.scrollIndicator().nativeElement.style.transform = `translateX(${scrollPercentage * (this.productCardsGridSection().nativeElement.clientWidth - indicatorWidth - scrollBarMargins)}px)`;
		}

		// we need to detect changes here because we are updating css styles using javascript
		// and it is not automatically changing the reference of the scroll indicator property in the DOM
		this.cdr.detectChanges();
	}

	private setGridTemplateAttributeRows(): void {
		if (isPlatformServer(this.platformId) || !this.coalescedProductDetails()?.length) {
			return;
		}

		// we need to set the grid template rows for the specs and attributes sections
		// based on the total number of attributes across all products
		const descAttrCount =
			this.coalescedProductDetails()[0]?.style?.descriptiveAttributes.length;
		const defAttrCount = this.coalescedProductDetails()[0]?.style?.definingAttributes.length;

		// the number of rows in the css grid will be equal to the number of attributes
		const specRowsVal = `repeat(${descAttrCount}, auto)`;
		const attrRowsVal = `repeat(${defAttrCount}, auto)`;
		const gtr = 'grid-template-rows';

		// this sets the grid-template-rows style rule for the attributes grid and the specs grid
		const specsRef = this.specsGridSection?.()?.nativeElement;
		const attrRef = this.attributesGridSection?.()?.nativeElement;

		if (specsRef) {
			this.renderer.setStyle(specsRef, gtr, specRowsVal);
		}
		if (attrRef) {
			this.renderer.setStyle(attrRef, gtr, attrRowsVal);
		}

		this.setVisibleContentPercentage();
	}

	trackByCompareBarProduct(index: number, item: CompareBarProduct): string {
		return item.ecode ? item.ecode : `${index.toString()}-empty`;
	}

	protected trackByCompareItemAttrribute(index: number, item: CompareItemAttribute): string {
		return `${index.toString()}-${item.identifier}`;
	}

	onTryAgain(): void {
		this.apiRetryCounter.update((c) => c + 1);
		this.tryAgainClicked.emit();
	}
}
